<template>
  <div class="highchart-container">
    <highcharts
      class="runtime-chart"
      :options="runtimeGraphOptions"
    />
  </div>
</template>

<script>
import moment from 'moment';
import unitPreferencesMixin from '@shared/mixins/unitPreferencesMixin';

import convertToCelsius from '@shared/lib/converter/convertToCelsius';

export default {
  name: 'RuntimeGraph',
  mixins: [unitPreferencesMixin],
  props: {
    runtimeGraphData: Array,
  },
  computed: {
    graphData() {
      const {
        partnerSettings: {
          TEMP_UNIT = '°F',
          DATE_FORMAT = 'MM-DD-YYYY',
        }
      } = this.$store.state.admin;

      const columnDate = [];
      const coolRuntime = [];
      const heatRuntime = [];
      const fanRuntime = [];
      const placeholderData = [];
      const maxOATemp = [];
      const minOATemp = [];

      this.runtimeGraphData.forEach((c) => {
        const {
          localDate,
          runtimes,
          maxTempAPI,
          minTempAPI,
        } = c;

        let coolRT = 0;
        let heatRT = 0;
        let fanRT = 0;
        if (runtimes) {
          const {
            Cool,
            Fan,
            Heat,
          } = runtimes;

          if (Cool && Cool.total) {
            coolRT += coolRT + Cool.total;
          }
          if (Heat && Heat.total) {
            heatRT += heatRT + Heat.total;
          }
          if (Fan && Fan.total) {
            fanRT += fanRT + Fan.total;
          }

          const cool = Math.round(coolRT / 3600);
          const heat = Math.round(heatRT / 3600);
          const fan = Math.round(fanRT / 3600);
          coolRuntime.push(cool);
          heatRuntime.push(heat);
          fanRuntime.push(fan);
        } else {
          coolRuntime.push(0);
          heatRuntime.push(0);
          fanRuntime.push(0);
        }
        let max = maxTempAPI;
        if (TEMP_UNIT === '°C') {
          max = convertToCelsius(max);
        }
        let min = minTempAPI;
        if (TEMP_UNIT === '°C') {
          min = convertToCelsius(min);
        }

        let date = moment(localDate).format('M/D/YYYY');
        if (DATE_FORMAT === 'DD-MM-YYYY') {
          date = moment(localDate).format('D/M/YYYY');
        }
        maxOATemp.push(max);
        minOATemp.push(min);
        columnDate.push(date);
        placeholderData.push(0);
      });
      return {
        columnDate,
        coolRuntime,
        heatRuntime,
        fanRuntime,
        placeholderData,
        maxOATemp,
        minOATemp,
      };
    },
    runtimeGraphOptions() {
      const {
        tempFormat,
      } = this;

      const {
        columnDate,
        coolRuntime,
        heatRuntime,
        fanRuntime,
        placeholderData,
        maxOATemp,
        minOATemp,
      } = this.graphData;

      return {
        chart: {
          zoomType: 'x',
          height: 400,
          backgroundColor: '#0f1734',
        },
        credits: {
          enabled: false,
        },
        title: {
          text: 'Runtime',
          style: {
            color: 'white',
          },
        },
        tooltip: {
          useHTML: true,
          nullFormat: 'Value is not available.',
          formatter() {
            const s = this.series.name;
            if (s === ' ' && this.y === 0) {
              return 'No cycle Detection';
            }

            return `${this.x}<br/>`
              + `${this.series.name}: <b>${this.y}</b>`;
          },
        },
        xAxis: [{
          categories: columnDate,
          reversed: new Date(columnDate[0]) > new Date(columnDate[columnDate.length - 1]),
          title: {
            text: 'Days',
            style: {
              color: 'white',
            },
          },
          labels: {
            style: {
              color: 'white',
            },
          },
        }],
        yAxis: [{
          min: 0,
          max: 24,
          title: {
            text: 'Hours',
            style: {
              color: 'white',
            },
          },
          stackLabels: {
            enabled: true,
            style: {
              fontWeight: 'bold',
              color: 'white',
            },
          },
          labels: {
            style: {
              color: 'white',
            },
          },
        }, {
          labels: {
            format: `{value}${tempFormat}`,
            style: {
              color: 'white',
            },
          },
          title: {
            text: 'Outside Air Temperature',
            style: {
              color: 'white',
            },
          },
          min: Math.min(...minOATemp, ...maxOATemp) - 4,
          max: Math.max(...minOATemp, ...maxOATemp) + 4,
          opposite: true,
        }],
        legend: {
          itemStyle: {
            color: 'white',
          },
          shadow: false,
          align: 'center',
          verticalAlign: 'bottom',
          x: 0,
          y: 0,
        },
        plotOptions: {
          column: {
            stacking: 'normal',
          },
        },
        series: [{
          type: 'column',
          yAxis: 0,
          xAxis: 0,
          name: 'Cool',
          color: '#6D94FF',
          borderColor: '#6D94FF',
          data: coolRuntime,
        }, {
          type: 'column',
          yAxis: 0,
          xAxis: 0,
          name: 'Heat',
          color: '#FF5B52',
          borderColor: '#FF5B52',
          data: heatRuntime,
        }, {
          type: 'column',
          yAxis: 0,
          xAxis: 0,
          name: 'Fan',
          color: '#4CD87D',
          borderColor: '#4CD87D',
          data: fanRuntime,
        }, {
          type: 'column',
          minPointLength: 5,
          name: ' ',
          yAxis: 0,
          xAxis: 0,
          color: 'transparent',
          borderColor: 'none',
          data: placeholderData,
        }, {
          type: 'scatter',
          yAxis: 1,
          xAxis: 0,
          name: 'Max OA T',
          color: '#ffa500',
          data: maxOATemp,
        }, {
          type: 'scatter',
          yAxis: 1,
          xAxis: 0,
          name: 'Min OA T',
          color: '#ffdb9a',
          data: minOATemp,
        }],
      };
    },
  },
};
</script>

<style lang="scss">
@import '../../../assets/styles/variables';

</style>
